import { useCallback, useEffect, useMemo, useState } from "react";
import AlertComponent from "../../../helpers/alert/AlertComponent";
import { filesServices } from "../../../helpers/services/FilesServices";
import {
  agregarProductoAlertedProductsSolicitud,
  eliminarProductoAlertedProductsSolicitud,
  gestionarAlertedProductsSolicitud,
  getAlertedProductsJornadaVigencia,
  getAlertedProductsSolicitudDetalle,
  getAlertedProductsTraza,
  subsanarAlertedProductsSolicitud,
} from "../../../entities/alerted-product";
import {
  formatDate,
  REVIEW_MODE_WITH_OBSERVATION, REVIEW_MODE_WITHOUT_OBSERVATION,
  DEFAULT_LEVANTAMIENTO_OBSERVACION,
} from "./alertManagementConstants";
import { mapPickedProductToAsociado } from "./getProductosAsociadosColumns";
import { buildProductoGestionarPayload, buildProductoIndeterminadoPayload, buildProductosAsociadosPayload } from "./subsanarPrices";
import { useAddProductModal } from "./useAddProductModal";
import { useIndeterminateRecalc } from "./useIndeterminateRecalc";

// Tonos que se alternan en la "Traza de eventos" (3 colores).
const TIMELINE_TONE_CYCLE = ["blue", "green", "amber"];

export const useAlertManagementRecord = ({ onGestionSuccess, unitOptions = [] } = {}) => {
  const [managingRecord, setManagingRecord] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isSubsanarMode, setIsSubsanarMode] = useState(false);
  const [isHistorialMode, setIsHistorialMode] = useState(false);
  const [historialData, setHistorialData] = useState(null);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [trazaData, setTrazaData] = useState(null);
  const [trazaLoading, setTrazaLoading] = useState(false);
  const [pdfViewer, setPdfViewer] = useState({ isOpen: false, url: null, title: "" });
  const [reviewMode, setReviewMode] = useState(null);
  const [reviewObservation, setReviewObservation] = useState("");
  const [reviewFiles, setReviewFiles] = useState([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmObservation, setConfirmObservation] = useState(DEFAULT_LEVANTAMIENTO_OBSERVACION);
  const [subsanarObservacion, setSubsanarObservacion] = useState("");
  const [subsanarDocumento, setSubsanarDocumento] = useState(null);
  const [subsanarSubmitting, setSubsanarSubmitting] = useState(false);
  const [addedProductos, setAddedProductos] = useState([]);
  const [deletedOrderDetailIds, setDeletedOrderDetailIds] = useState([]);
  const [deletingOrderDetailId, setDeletingOrderDetailId] = useState(null);
  // Nuevo precio de venta editado por fila (keyed por rowKey del producto asociado).
  const [salePrices, setSalePrices] = useState({});
  // Formulario editable del nuevo producto para la jornada (flujo INDETERMINADO).
  const [indeterminateForm, setIndeterminateForm] = useState({
    nombre: "", especificacion_tecnica: "", unidad: null, marca: "",
  });

  // Vigencia de la jornada (flujo GESTIONAR / PRODUCTO INDETERMINADO).
  const [jornadaVigencia, setJornadaVigencia] = useState(null);
  const [jornadaVigenciaLoading, setJornadaVigenciaLoading] = useState(false);

  // Recálculo de valores de mercado (Sub. Operativa) para PRODUCTO INDETERMINADO.
  const recalc = useIndeterminateRecalc();

  const isIndeterminate = useMemo(
    () => (managingRecord?.tipoGestion || "").toUpperCase().includes("INDETERMINADO"),
    [managingRecord]
  );

  // El "Nuevo precio de venta" solo aplica al flujo de AJUSTE DE PRECIO.
  const isPriceAdjustment = useMemo(
    () => (managingRecord?.tipoGestion || "").toUpperCase().includes("AJUSTE DE PRECIO"),
    [managingRecord]
  );

  const resetRecord = useCallback((pdfUrl) => {
    setManagingRecord(null); setDetailData(null); setIsViewMode(false); setIsSubsanarMode(false);
    setIsHistorialMode(false); setHistorialData(null); setHistorialLoading(false);
    setTrazaData(null); setTrazaLoading(false);
    setReviewMode(null); setReviewObservation(""); setReviewFiles([]);
    setIsConfirmOpen(false);
    setSubsanarObservacion(""); setSubsanarDocumento(null); setSubsanarSubmitting(false);
    setAddedProductos([]); setDeletedOrderDetailIds([]); setDeletingOrderDetailId(null); setSalePrices({});
    setIndeterminateForm({ nombre: "", especificacion_tecnica: "", unidad: null, marca: "" });
    setJornadaVigencia(null); setJornadaVigenciaLoading(false);
    recalc.reset();
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfViewer({ isOpen: false, url: null, title: "" });
  }, [recalc]);

  const loadDetalle = useCallback(async (record) => {
    setDetailData(null);
    if (!record?.id) return;
    setDetailLoading(true);
    try { setDetailData(await getAlertedProductsSolicitudDetalle(record.id)); }
    catch { AlertComponent.error("Error", "No fue posible cargar el detalle de la solicitud."); }
    finally { setDetailLoading(false); }
  }, []);

  const handleGestionar = useCallback(async (record) => {
    setManagingRecord(record); setIsViewMode(false);
    setReviewMode(null); setReviewObservation(""); setReviewFiles([]);
    setIsConfirmOpen(false);
    setTrazaData(null);
    setJornadaVigencia(null);
    recalc.reset();
    loadDetalle(record);
    if (record?.id) {
      setTrazaLoading(true);
      getAlertedProductsTraza(record.id)
        .then(setTrazaData)
        .catch(() => setTrazaData(null))
        .finally(() => setTrazaLoading(false));
    }
  }, [loadDetalle, recalc]);

  const handleVer = useCallback(async (record) => {
    setManagingRecord(record); setIsViewMode(true); setDetailData(null);
    loadDetalle(record);
  }, [loadDetalle]);

  const handleSubsanar = useCallback((record) => {
    setManagingRecord(record); setIsViewMode(false); setIsSubsanarMode(true);
    setSubsanarObservacion(""); setSubsanarDocumento(null);
    setAddedProductos([]); setDeletedOrderDetailIds([]); setDeletingOrderDetailId(null); setSalePrices({});
    setIndeterminateForm({ nombre: "", especificacion_tecnica: "", unidad: null, marca: "" });
    loadDetalle(record);
  }, [loadDetalle]);

  const setIndeterminateField = useCallback(
    (field, value) => setIndeterminateForm((prev) => ({ ...prev, [field]: value })),
    []
  );

  // Precarga los campos editables del nuevo producto con lo que llega del detalle
  // (productos_asociados[0]) para el flujo INDETERMINADO en modo subsanación.
  useEffect(() => {
    if (!isSubsanarMode || !isIndeterminate) return;
    const producto = detailData?.productosAsociados?.[0];
    if (!producto) return;
    setIndeterminateForm({
      nombre: producto.nombre_sugerido ?? "",
      especificacion_tecnica: producto.especTecnicaSugerida ?? "",
      // `unidad_sugerida` llega como id; el label se resuelve contra /api/lista/unidades/.
      unidad:
        producto.unidadSugeridaId != null
          ? {
              value: producto.unidadSugeridaId,
              label:
                unitOptions.find((u) => u.value === producto.unidadSugeridaId)?.label ?? "",
            }
          : null,
      marca: producto.marca_sugerida ?? "",
    });
  }, [detailData, isSubsanarMode, isIndeterminate, unitOptions]);

  // Consulta la vigencia de la jornada al entrar a GESTIONAR un PRODUCTO INDETERMINADO.
  // El id de jornada confiable llega en el detalle (detailData.jornada.id).
  useEffect(() => {
    if (isViewMode || isSubsanarMode || !isIndeterminate) return;
    const idJornada = detailData?.jornada?.id;
    if (idJornada == null) return;
    let active = true;
    setJornadaVigenciaLoading(true);
    getAlertedProductsJornadaVigencia(idJornada)
      .then((vigencia) => { if (active) setJornadaVigencia(vigencia); })
      .catch(() => { if (active) setJornadaVigencia(null); })
      .finally(() => { if (active) setJornadaVigenciaLoading(false); });
    return () => { active = false; };
  }, [detailData, isViewMode, isSubsanarMode, isIndeterminate]);

  const handleAddProducto = useCallback(async (product) => {
    if (!managingRecord?.id || !product?.orderDetailId) return;
    try {
      await agregarProductoAlertedProductsSolicitud({
        idEncabezado: managingRecord.id,
        idOrdenDetalle: product.orderDetailId,
      });
      setAddedProductos((prev) => [...prev, mapPickedProductToAsociado(product)]);
      AlertComponent.success("Producto agregado", "El producto fue agregado a la solicitud correctamente.");
    } catch {
      AlertComponent.error("Error", "No fue posible agregar el producto a la solicitud.");
    }
  }, [managingRecord]);

  const handleDeleteProducto = useCallback(async (product) => {
    const idOrdenDetalle = product?.id_orden_detalle;
    if (!managingRecord?.id || idOrdenDetalle == null) return;
    setDeletingOrderDetailId(idOrdenDetalle);
    try {
      await eliminarProductoAlertedProductsSolicitud({
        idEncabezado: managingRecord.id,
        idOrdenDetalle,
      });
      setAddedProductos((prev) => prev.filter((p) => p.id_orden_detalle !== idOrdenDetalle));
      setDeletedOrderDetailIds((prev) => [...prev, idOrdenDetalle]);
      AlertComponent.success("Producto eliminado", "El producto fue eliminado de la solicitud correctamente.");
    } catch {
      AlertComponent.error("Error", "No fue posible eliminar el producto de la solicitud.");
    } finally {
      setDeletingOrderDetailId(null);
    }
  }, [managingRecord]);

  const handleSalePriceChange = useCallback(
    (rowKey, value) => setSalePrices((prev) => ({ ...prev, [rowKey]: value })),
    []
  );

  const subsanarProductos = useMemo(
    () =>
      [...(detailData?.productosAsociados ?? []), ...addedProductos].filter(
        (p) => !deletedOrderDetailIds.includes(p.id_orden_detalle)
      ),
    [detailData, addedProductos, deletedOrderDetailIds]
  );

  const addedOrderDetailIds = useMemo(
    () => subsanarProductos.map((p) => p.id_orden_detalle).filter(Boolean),
    [subsanarProductos]
  );

  const addProductModal = useAddProductModal({
    jornadaId: detailData?.jornada?.id ?? null,
    addedOrderDetailIds,
    onAdd: handleAddProducto,
  });

  const handleSubsanarSubmit = useCallback(async () => {
    if (!subsanarObservacion.trim()) {
      AlertComponent.warning("Observación requerida", "Debe diligenciar la nueva observación justificativa.");
      return;
    }
    if (!managingRecord?.id) return;
    setSubsanarSubmitting(true);
    try {
      // INDETERMINADO gestiona un único item: se envía como objeto `producto` con
      // los campos editados del "Nuevo producto para el catálogo de la jornada".
      // El resto de flujos envían el array `productos_asociados`.
      const payload = isIndeterminate
        ? {
            producto: buildProductoIndeterminadoPayload(
              detailData?.productosAsociados?.[0] ?? {},
              indeterminateForm
            ),
          }
        : { productosAsociados: buildProductosAsociadosPayload(subsanarProductos, salePrices) };
      await subsanarAlertedProductsSolicitud({
        idEncabezado: managingRecord.id,
        observacion: subsanarObservacion,
        documento: subsanarDocumento,
        ...payload,
      });
      AlertComponent.success("Subsanación enviada", "La solicitud fue reenviada a revisión correctamente.");
      onGestionSuccess?.();
      resetRecord(pdfViewer.url);
    } catch {
      AlertComponent.error("Error", "No fue posible enviar la subsanación.");
    } finally {
      setSubsanarSubmitting(false);
    }
  }, [managingRecord, subsanarObservacion, subsanarDocumento, subsanarProductos, salePrices, isIndeterminate, detailData, indeterminateForm, pdfViewer.url, resetRecord, onGestionSuccess]);
  const handleHistorial = useCallback(async (record) => {
    if (!record?.id) return;
    setManagingRecord(record); setIsViewMode(false); setIsSubsanarMode(false);
    setIsHistorialMode(true); setHistorialData(null); setHistorialLoading(true);
    try {
      setHistorialData(await getAlertedProductsTraza(record.id));
    } catch {
      AlertComponent.error("Error", "No fue posible cargar el historial de la solicitud.");
    } finally {
      setHistorialLoading(false);
    }
  }, []);
  const handleBackToTable = useCallback(() => resetRecord(pdfViewer.url), [resetRecord, pdfViewer.url]);

  const handleViewDocument = useCallback(async (doc) => {
    if (!doc?.rutaArchivo) return;
    try {
      const res = await filesServices.downloadFile(doc.rutaArchivo);
      if (!res?.blob) return;
      setPdfViewer({ isOpen: true, url: URL.createObjectURL(res.blob), title: doc.nombre || "documento.pdf" });
    } catch { AlertComponent.error("Error", "No fue posible abrir el documento."); }
  }, []);

  const handleDownloadDocument = useCallback(async (doc) => {
    if (!doc?.rutaArchivo) return;
    try {
      const res = await filesServices.downloadFile(doc.rutaArchivo);
      if (!res?.blob) return;
      const url = URL.createObjectURL(res.blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.nombre || "documento";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { AlertComponent.error("Error", "No fue posible descargar el documento."); }
  }, []);

  const closePdfViewer = useCallback(() => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ isOpen: false, url: null, title: "" });
  }, [pdfViewer.url]);

  const handleDownloadFromViewer = useCallback(() => {
    if (!pdfViewer.url) return;
    const a = document.createElement("a");
    a.href = pdfViewer.url; a.download = pdfViewer.title;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }, [pdfViewer]);

  const handleSelectWithObservation = useCallback(() => {
    setReviewMode(REVIEW_MODE_WITH_OBSERVATION);
    if (reviewObservation === "Sin observación") setReviewObservation("");
  }, [reviewObservation]);

  const handleSelectWithoutObservation = useCallback(() => { setReviewMode(REVIEW_MODE_WITHOUT_OBSERVATION); setConfirmObservation(DEFAULT_LEVANTAMIENTO_OBSERVACION); setIsConfirmOpen(true); }, []);
  const handleReviewFilesChange = useCallback((e) => {
    const selected = Array.from(e?.target?.files ?? []);
    const isPdf = (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);
    const pdfFiles = selected.filter(isPdf);
    if (pdfFiles.length !== selected.length) {
      AlertComponent.warning("Formato no permitido", "Solo se pueden anexar archivos PDF en los adjuntos del revisor.");
      if (e?.target) e.target.value = "";
    }
    setReviewFiles(pdfFiles);
  }, []);

  const handleSubmitWithObservation = useCallback(async () => {
    if (!reviewObservation.trim()) {
      AlertComponent.warning("Observación requerida", "Debe diligenciar la observación del revisor para continuar.");
      return;
    }
    if (!managingRecord?.id) return;
    try {
      await gestionarAlertedProductsSolicitud({
        idEncabezado: managingRecord.id,
        idEstado: "5260",
        observacion: reviewObservation,
        documento: reviewFiles[0] ?? null,
        // PRODUCTO INDETERMINADO: se adjunta el producto con los valores recalculados.
        producto: isIndeterminate
          ? buildProductoGestionarPayload(
              detailData?.productosAsociados?.[0] ?? {},
              recalc.values,
              recalc.result
            )
          : null,
      });
      AlertComponent.success("Enviado a subsanación", "La solicitud fue enviada a subsanación correctamente.");
      onGestionSuccess?.();
      resetRecord(pdfViewer.url);
    } catch {
      AlertComponent.error("Error", "No fue posible enviar la solicitud a subsanación.");
    }
  }, [managingRecord, reviewObservation, reviewFiles, isIndeterminate, detailData, recalc.values, recalc.result, pdfViewer.url, resetRecord, onGestionSuccess]);

  const handleConfirmWithoutObservation = useCallback(async () => {
    setIsConfirmOpen(false);
    if (!managingRecord?.id) return;
    try {
      await gestionarAlertedProductsSolicitud({
        idEncabezado: managingRecord.id,
        idEstado: "5261",
        observacion: confirmObservation.trim() || DEFAULT_LEVANTAMIENTO_OBSERVACION,
        // PRODUCTO INDETERMINADO: se adjunta el producto con los valores recalculados.
        producto: isIndeterminate
          ? buildProductoGestionarPayload(
              detailData?.productosAsociados?.[0] ?? {},
              recalc.values,
              recalc.result
            )
          : null,
      });
      AlertComponent.success("Gestión exitosa", "La solicitud fue resuelta correctamente.");
      onGestionSuccess?.();
      resetRecord(pdfViewer.url);
    } catch {
      AlertComponent.error("Error", "No fue posible gestionar la solicitud.");
    }
  }, [managingRecord, confirmObservation, isIndeterminate, detailData, recalc.values, recalc.result, pdfViewer.url, resetRecord, onGestionSuccess]);

  const documents = useMemo(() => detailData?.documentos ?? [], [detailData]);

  // La "Traza de eventos" consume el mismo servicio del botón "Historial".
  // Cada item usa el "tipo" como título y "fcrea - correo" como metadato,
  // alternando 3 tonos de color por posición.
  const timeline = useMemo(() => {
    const eventos = trazaData?.eventos ?? [];
    return eventos.map((e, i) => ({
      id: e.rowKey ?? `traza-${i}`,
      title: e.tipo || "—",
      meta: `${formatDate(e.fecha)} - ${e.usuario || "—"}`,
      tone: TIMELINE_TONE_CYCLE[i % TIMELINE_TONE_CYCLE.length],
    }));
  }, [trazaData]);

  return {
    managingRecord, detailData, detailLoading, isViewMode, isSubsanarMode, pdfViewer,
    isHistorialMode, historialData, historialLoading, trazaLoading,
    reviewMode, reviewObservation, setReviewObservation, reviewFiles, isConfirmOpen, documents, timeline,
    confirmObservation, setConfirmObservation,
    subsanarObservacion, setSubsanarObservacion, subsanarDocumento, setSubsanarDocumento, subsanarSubmitting,
    subsanarProductos, addProductModal, handleDeleteProducto, deletingOrderDetailId,
    salePrices, handleSalePriceChange,
    isIndeterminate, isPriceAdjustment, indeterminateForm, setIndeterminateField, recalc,
    jornadaVigencia, jornadaVigenciaLoading,
    handleGestionar, handleVer, handleSubsanar, handleSubsanarSubmit, handleHistorial, handleBackToTable,
    handleViewDocument, handleDownloadDocument, closePdfViewer, handleDownloadFromViewer,
    handleSelectWithObservation, handleSelectWithoutObservation,
    handleReviewFilesChange, handleSubmitWithObservation, handleConfirmWithoutObservation,
    setIsConfirmOpen,
  };
};